import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    TableBody,
    TableCell,
    TableRow,
    Theme,
    Tooltip,
    Typography,
    useTheme,
    Skeleton
} from "@mui/material";
import {
    ColorMap,
    MutualFundTableBodyProps,
    SchemeInfoProps,
    TableBodyData,
    TableHeaderData,
    TableItemCheckboxProps
} from "../../types";
import {
    BorderStarFilled,
    CheckIcon,
    GoldenStarFilled,
    UnCheckIcon
} from "../../../../assets/SvgIcons";
import { useState } from "react";
import { checkImageExists, getValueColor } from "../../../../utils/utilityFunctions";
import { useSelector } from "react-redux";
import { RootStateType } from "../../../../redux-store/reducers";
import { useHistory } from "react-router";
import dayjs from "dayjs";

const colorMap: ColorMap = {
    low: 'success.500',
    medium: 'warning.500',
    "very high": 'error.500'
}

function SchemeInfo({
    company,
    schemeName,
    type,
    subType,
    risk,
    onSchemeClick = () => { return; }
}: SchemeInfoProps) {
    const amcIds = ['BDOTR', 'BPIAMC']
    const checkAmcId = amcIds.includes(company ?? '') ? company?.toLowerCase() : company;
    return (
        <Box sx={{ display: "flex", alignItems: "center", flexDirection: 'row', gap: '8px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {company &&
                <img
                    src={checkImageExists(`${checkAmcId}`) ? `/images/amcIcons/${checkAmcId}.svg` : `/images/amcIcons/default.svg`}
                    alt={company}
                    width={25}
                    height={25}
                    style={{
                        objectFit: "contain",
                        borderRadius: "8px",
                        alignSelf: 'flex-start',
                    }}
                />
            }
            <Box
              role="button"
                tabIndex={0}
                aria-label={`View details for ${schemeName}`}
                sx={{
                    cursor: "pointer",
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                }}
                onClick={(event: React.MouseEvent) => onSchemeClick(event)}
                onKeyDown={(event: React.KeyboardEvent) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onSchemeClick(event);
                    }
                }}
            >
                <Tooltip title={schemeName} followCursor>
                    <Typography
                        variant="subtitle_500"
                        sx={(theme: Theme) => ({
                            color: theme.palette.text.primary,
                            whiteSpace: { xs: 'normal', sm: 'nowrap' },
                            width: '100%',
                            overflow: 'hidden',
                            overflowWrap: 'anywhere',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            '&:hover': {
                                color: theme.palette.primary[500],
                            },
                        })}
                    >
                        {schemeName}
                    </Typography>
                </Tooltip>
                <Grid
                    container
                    sx={{
                        marginTop: { xs: "10px", sm: '5px' },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        alignItems: "center",
                        columnGap: "8px",
                        rowGap: { xs: '6px', sm: 0 },
                        maxWidth: '100%',
                    }}
                >
                    <Grid item sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                        <Typography
                            variant="overline_400"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.secondary,
                            })}
                        >
                            {type}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box
                            sx={{
                                width: "3px",
                                height: "3px",
                                backgroundColor: "text.secondary",
                                borderRadius: "50%",
                            }}
                        />
                    </Grid>
                    <Grid item sx={{ whiteSpace: { xs: 'normal', sm: 'nowrap' } }}>
                        <Typography
                            variant="overline_400"
                            sx={(theme: Theme) => ({
                                color: theme.palette.text.secondary,
                                width: '100%',
                            })}
                        >
                            {subType}
                        </Typography>
                    </Grid>
                    {/* <Grid item>
                        <Box
                            sx={(theme: Theme) => ({
                                width: "3px",
                                height: "3px",
                                backgroundColor: theme.palette.common.black,
                                borderRadius: "50%",
                                display: (!risk || risk === "NULL") ? "none" : "block",
                            })}
                        />
                    </Grid>
                    <Grid item sx={{ whiteSpace: "nowrap" }}>
                        <Typography
                            variant="body_400"
                            sx={(theme: Theme) => ({
                                color: theme.palette.common.black,
                                display: (!risk || risk === "NULL") ? "none" : "block",
                            })}
                        >
                            {risk} Risk
                        </Typography>
                    </Grid> */}
                </Grid>

            </Box >
        </Box>
    )
}

function TableItemCheckbox({
    company,
    value,
    schemeName,
    type = "",
    subType = "",
    risk = "",
    checked,
    onCheckboxClick,
    onSchemeClick = () => { return; }
}: TableItemCheckboxProps) {
    return (
        <FormControlLabel
            value={value}
            control={
                <Checkbox
                    size="small"
                    checkedIcon={<CheckIcon />}
                    icon={<UnCheckIcon />}
                    sx={{
                        '&:hover': {
                            backgroundColor: 'transparent',
                        },
                        alignSelf: 'flex-start'
                    }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={onCheckboxClick}
                    checked={checked}
                    inputProps={{
                        'aria-label': `Select ${schemeName}`,
                        onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                (event.currentTarget as HTMLInputElement).click();
                            }
                        }
                    }as React.InputHTMLAttributes<HTMLInputElement>}
                />
            }
            label={
                <SchemeInfo
                    company={company}
                    schemeName={schemeName}
                    type={type}
                    subType={subType}
                    risk={risk}
                    onSchemeClick={(event?: React.MouseEvent | React.KeyboardEvent) => {
                        if (event) {
                            event.stopPropagation();
                            event.preventDefault();
                        }
                        onSchemeClick();
                    }}
                />
            }
            sx={{
                display: 'flex',
                "& .MuiCheckbox-root": {
                    padding: { xs: '2px', sm: 'unset' }
                },
                "& .MuiFormControlLabel-label": {
                    marginLeft: "15px",
                    width: "100%",
                }
            }}
            onClick={(event) => {
                event.preventDefault()
            }}
        />
    );
}


export default function MutualFundTableBody(
    {
        tableBodyData,
        tableBodyRowCustomStyles = {},
        showCheckbox = true,
        handleCheckboxClick = () => { return; },
        handleSchemeClick = () => { return; },
        selectedCategory = ""
    }: MutualFundTableBodyProps
): JSX.Element {
    const theme = useTheme();
    const history = useHistory()
    const { pathname } = history.location
    const isCheckBoxHidden = !!pathname.includes('product');
    const isCategoryNFO = selectedCategory === "NFO";
    return (
        <>
            {tableBodyData.map((obj: TableBodyData) => (
                <TableRow
                    key={obj.scheme}
                    sx={{
                        '& td': {
                            paddingTop: "10px",
                            paddingBottom: "10px",
                            borderBottom: `0.5px solid ${theme.palette.text.secondary}B2`,
                        },
                        ...tableBodyRowCustomStyles
                    }}
                >
                    <TableCell
                        align="left"
                        sx={{
                            maxWidth: "300px",
                            overflow: "hidden",
                        }}
                    >
                        {!isCheckBoxHidden ?
                            <TableItemCheckbox
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                value={obj?.scheme ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                checked={!!obj?.checked}
                                onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxClick(obj?.scheme ?? "", event.target.checked)}
                                onSchemeClick={() => handleSchemeClick(obj)}
                            /> : <SchemeInfo
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                onSchemeClick={() => handleSchemeClick(obj)}
                            />}
                    </TableCell>
                    {/* added to the table for narc */}
                    {!isCategoryNFO && (
                        <>
                            <TableCell align="center">
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        border: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                        borderRadius: '20px',
                                        padding: '12px',
                                        width: 'fit-content',
                                        mx: 'auto',
                                        gap: '4px'
                                    }}
                                >
                                    {
                                        obj?.fundRating?.trim().toLowerCase() === 'not rated'
                                            ?
                                            (
                                                <>
                                                    <Typography
                                                        variant="overline_400"
                                                        sx={(theme) => ({
                                                            color: theme.palette.text.primary,
                                                        })}
                                                    >
                                                        Not Rated
                                                    </Typography>
                                                </>
                                            )
                                            : obj?.fundRating && !Number.isNaN(Number(obj?.fundRating)) && Number(obj?.fundRating) > 0 ? (
                                                <>
                                                    <Typography
                                                        variant="overline_400"
                                                        sx={(theme) => ({
                                                            color: theme.palette.text.primary,
                                                            mt: 0.3
                                                        })}
                                                    >
                                                        {Number(Math.floor(obj?.fundRating))}
                                                    </Typography>
                                                    <GoldenStarFilled />
                                                </>
                                            ) : (
                                                <>
                                                    <Typography
                                                        variant="overline_400"
                                                        sx={(theme) => ({
                                                            color: theme.palette.text.primary,

                                                        })}
                                                    >
                                                        Not Rated
                                                    </Typography>

                                                </>
                                            )
                                    }
                                </Box>
                            </TableCell>

                            <TableCell align="center">
                                <Typography
                                    variant="body_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.year1Return && !Number.isNaN(Number(obj.year1Return)) ? `${Number(obj.year1Return).toFixed(2)}%` : "0%"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.year3Return && !Number.isNaN(Number(obj.year3Return)) ? `${Number(obj.year3Return).toFixed(2)}%` : "0%"}
                                </Typography>
                            </TableCell>
                            <TableCell align="center">
                                <Typography
                                    variant="body_400"
                                    sx={(theme: Theme) => ({
                                        color: theme.palette.text.primary
                                    })}
                                >
                                    {obj?.year5Return && !Number.isNaN(Number(obj.year5Return)) ? `${Number(obj.year5Return).toFixed(2)}%` : "0%"}
                                </Typography>
                            </TableCell>
                        </>
                    )}
                    {
                        isCategoryNFO && (
                            <>
                                <TableCell align="center">
                                    <Typography
                                        variant="body_400"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {obj?.issueOpen ? dayjs(obj?.issueOpen).format("DD/MM/YYYY") : "-"}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Typography
                                        variant="body_400"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.primary
                                        })}
                                    >
                                        {obj?.issueStatedClose ? dayjs(obj?.issueStatedClose).format("DD/MM/YYYY") : "-"}
                                    </Typography>
                                </TableCell>
                            </>
                        )
                    }
                </TableRow>
            ))}
        </>
    )
}


export function MobileResponsiveMutualFundBody({
    data,
    showCheckbox = true,
    handleCheckboxClick = () => { return; },
    handleSchemeClick = () => { return; },
    tableHeaderData
}: {
    data: TableBodyData[],
    showCheckbox?: boolean,
    handleCheckboxClick?: (scheme: string, checked: boolean) => void,
    handleSchemeClick?: (obj: TableBodyData) => void,
    tableHeaderData: TableHeaderData[]
}): JSX.Element {
    const history = useHistory();
    const { pathname } = history.location;
    const isCheckBoxHidden = !!pathname.includes("product");

    const getFormattedValue = (obj: TableBodyData, key: string) => {
        switch (key) {
            case "year1Return":
            case "year3Return":
            case "year5Return":
                return obj?.[key] && !Number.isNaN(Number(obj[key]))
                    ? `${Number(obj[key]).toFixed(2)}%`
                    : "0%";
            case "intFundRating": {
                if (obj?.fundRating?.trim().toLowerCase() === "not rated") return "Not Rated";
                if (obj?.fundRating && !Number.isNaN(Number(obj?.fundRating)) && Number(obj?.fundRating) > 0) {
                    return Math.floor(Number(obj?.fundRating)).toString();
                }
                return "Not Rated";
            }
            default:
                return obj?.[key] ?? "-";
        }
    };

    const getValueColor = (key: string, value: any, theme: Theme) => {
        if (["year1Return", "year3Return", "year5Return"].includes(key)) {
            const num = Number(value);
            if (!Number.isNaN(num)) {
                if (num > 0) return theme.palette.success.main;
                if (num < 0) return theme.palette.error.main;
            }
        }
        return theme.palette.text.primary;
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {data.map((obj, index) => (
                <Box
                    key={`${obj.scheme}-${index}`}
                    sx={(theme: Theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: "12px",
                        padding: "16px",
                        backgroundColor: theme.palette.background.paper,
                        display: "flex",
                        flexDirection: "column",
                        gap: 2
                    })}
                >
                    {/* Scheme Info */}
                    <Box>
                        {!isCheckBoxHidden && showCheckbox ? (
                            <TableItemCheckbox
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                value={obj?.scheme ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                checked={!!obj?.checked}
                                onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) =>
                                    handleCheckboxClick(obj?.scheme ?? "", event.target.checked)
                                }
                                onSchemeClick={() => handleSchemeClick(obj)}
                            />
                        ) : (
                            <SchemeInfo
                                company={obj?.company ?? ""}
                                schemeName={obj?.schemeName ?? ""}
                                type={obj?.categoryName ?? ""}
                                subType={obj?.subCategoryName ?? ""}
                                risk={obj?.riskType ?? ""}
                                onSchemeClick={() => handleSchemeClick(obj)}
                            />
                        )}
                    </Box>

                    {/* Details grid */}
                    <Box
                        display="grid"
                        gridTemplateColumns="1fr 1fr"
                        gap={2}
                        textAlign="center"
                    >
                        {tableHeaderData
                            .filter(header => header.id !== "schemeName")
                            .map(header => (
                                <Box key={header.id}>
                                    {/* Label (top) */}
                                    <Typography
                                        variant="overline"
                                        sx={(theme: Theme) => ({
                                            color: theme.palette.text.secondary,
                                            fontSize: "12px",
                                            mb: 0.5
                                        })}
                                    >
                                        {header.label}
                                    </Typography>

                                    {/* Value (below) */}
                                    {header.id === "intFundRating" ? (
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            gap={0.5}
                                            sx={(theme: Theme) => ({
                                                border: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                                borderRadius: "20px",
                                                padding: "6px 10px",
                                                width: "fit-content",
                                                mx: "auto",

                                            })}
                                        >
                                            {obj?.fundRating && !Number.isNaN(Number(obj?.fundRating)) && Number(obj?.fundRating) > 0 ? (
                                                <>
                                                    <Typography
                                                        variant="body_500"
                                                        sx={(theme: Theme) => ({
                                                            color: theme.palette.text.primary,
                                                            fontSize: "14px",
                                                        })}
                                                    >
                                                        {Math.floor(Number(obj?.fundRating))}
                                                    </Typography>
                                                    <GoldenStarFilled />
                                                </>
                                            ) : (
                                                <Typography
                                                    variant="body_500"
                                                    sx={(theme: Theme) => ({
                                                        color: theme.palette.text.secondary,
                                                        fontSize: "13px",
                                                    })}
                                                >
                                                    Not Rated
                                                </Typography>
                                            )}
                                        </Box>
                                    ) : (
                                        <Typography
                                            variant="body_500"
                                            sx={(theme: Theme) => ({
                                                color: getValueColor(header.id, obj?.[header.id], theme),
                                                fontSize: "14px",
                                            })}
                                        >
                                            {getFormattedValue(obj, header.id)}
                                        </Typography>
                                    )}


                                </Box>
                            ))}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}



// export function MutualFundResponsiveBody({
//     tableBodyData,
//     tableBodyRowCustomStyles = {},
//     showCheckbox = true,
//     handleCheckboxClick = () => { return; },
//     handleSchemeClick = () => { return; },
//     tableHeaderData = [],
// }: MutualFundTableBodyProps): JSX.Element {

//     const { palette } = useTheme();
//     const history=useHistory();
//     const { pathname } = history.location;
//     const isCheckBoxHidden = !!pathname.includes("product");
//     const [tableHeaderKey, setTableHeaderKey] = useState<string>(
//         tableHeaderData[1]?.key || tableHeaderData[1]?.id || ""
//     );

//     const style: any = { color: palette.text.secondary, fontWeight: 500, fontSize: '14px', textTransform: 'uppercase', textWrap: 'nowrap', paddingRight: 0, paddingLeft: 0 }
//     return (
//         <TableBody>
//             {tableBodyData.map((item: any, index: number) => (
//                 <TableRow key={index}>
//                     <TableCell
//                         style={{ ...style }}
//                         align='left'
//                     >
//                         <Typography
//                             variant="subtitle_500"
//                             sx={(theme: Theme) => ({
//                                 // color: theme.palette.primary[500],
//                                 textOverflow: 'ellipsis',
//                                 whiteSpace: 'nowrap',
//                                 overflow: 'hidden',
//                                 maxWidth: '200px',
//                                 width: '100%',
//                             })}
//                         >
//                             {!isCheckBoxHidden ?
//                                 <TableItemCheckbox
//                                     schemeName={item?.schemeName ?? ""}
//                                     value={item?.scheme ?? ""}
//                                     type={item?.categoryName ?? ""}
//                                     subType={item?.subCategoryName ?? ""}
//                                     risk={item?.riskType ?? ""}
//                                     checked={!!item?.checked}
//                                     onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) => handleCheckboxClick(item?.scheme ?? "", event.target.checked)}
//                                     onSchemeClick={() => handleSchemeClick(item)}
//                                 /> : <SchemeInfo
//                                     company={item?.company ?? ""}
//                                     schemeName={item?.schemeName ?? ""}
//                                     type={item?.categoryName ?? ""}
//                                     subType={item?.subCategoryName ?? ""}
//                                     risk={item?.riskType ?? ""}
//                                     onSchemeClick={() => handleSchemeClick(item)}
//                                 />}
//                             {/* {item?.[tableHeaderData[0].id]} */}
//                         </Typography>
//                     </TableCell>
//                     <TableCell
//                         style={{ ...style }}
//                         align='center'
//                     >
//                         <Typography
//                             variant="subtitle_500"
//                             sx={(theme: Theme) => ({
//                                 color: theme.palette.text.primary,
//                             })}
//                         >
//                             {tableHeaderKey.includes('.') ? item?.[tableHeaderKey.split('.')[0]][tableHeaderKey.split('.')[1]] : item?.[tableHeaderKey]}
//                         </Typography>
//                     </TableCell>
//                 </TableRow>
//             ))}
//         </TableBody>
//     )
// }

export function SchemeBoxResponsiveBody({
    tableBodyData,
    tableBodyRowCustomStyles = {},
    showCheckbox = true,
    handleCheckboxClick = () => {
        return;
    },
    handleSchemeClick = () => {
        return;
    },
    tableHeaderData = [],
    selectedCategory = "",
}: MutualFundTableBodyProps): JSX.Element {
    const { palette } = useTheme();
    const history = useHistory();
    const { pathname } = history.location;
    const isCheckBoxHidden = !!pathname.includes("product");
    const isNFOSelected = selectedCategory === "NFO";

    return (
        <Box
            sx={{
                padding: "5px",
                width: "100%",
                maxWidth: "100%",
                overflow: "hidden",
                height: "100%",
                paddingBottom: '40px'
            }}
        >
            {tableBodyData.map((item: any, index: number) => {
                return (
                    <Box
                        key={index}
                        sx={(theme: Theme) => ({
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: "8px",
                            padding: "12px 15px ",
                            marginBottom: "12px",
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            cursor: "pointer",
                            width: "100%",
                            maxWidth: "100%",
                            boxSizing: "border-box",
                            overflow: "auto",

                        })}
                        onClick={() => handleSchemeClick(item)}
                    >
                        {/* reference:Header and checkBoc this is  */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "8px",
                                mb: 2,
                                width: "100%",
                            }}
                        >
                            {/*Mine  reference:If checkbox show tableitemcheckbox */}
                            {!isCheckBoxHidden && showCheckbox ? (
                                <TableItemCheckbox
                                    company={item?.company ?? ""}
                                    schemeName={item?.schemeName ?? ""}
                                    value={item?.scheme ?? ""}
                                    type={item?.categoryName ?? ""}
                                    subType={item?.subCategoryName ?? ""}
                                    risk={item?.riskType ?? ""}
                                    checked={!!item?.checked}
                                    onCheckboxClick={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        handleCheckboxClick(item?.scheme ?? "", event.target.checked)
                                    }
                                    onSchemeClick={() => handleSchemeClick(item)}
                                />
                            ) : (
                                <SchemeInfo
                                    company={item?.company ?? ""}
                                    schemeName={item?.schemeName ?? ""}
                                    type={item?.categoryName ?? ""}
                                    subType={item?.subCategoryName ?? ""}
                                    risk={item?.riskType ?? ""}
                                    onSchemeClick={() => handleSchemeClick(item)}
                                />
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 2,
                                p: 1.5,
                                backgroundColor: "rgba(0,0,0,0.02)",
                                borderRadius: "6px",
                                width: "100%",
                                boxSizing: "border-box",
                            }}
                        >
                            {tableHeaderData.map((header, headerIndex) => {
                                const headerKey = header.key || header.id;
                                const headerLabel = header.label;
                                if (headerKey === 'schemeName' || headerKey === 'scheme' || headerLabel.toLowerCase().includes('scheme')) {
                                    return null;
                                }

                                // fetching value from based on header key 
                                const getValue = (item: any, key: string) => {
                                    if (key.includes('.')) {
                                        const [parentKey, childKey] = key.split('.');
                                        return item?.[parentKey]?.[childKey];
                                    }
                                    return item?.[key];
                                };

                                const value = getValue(item, headerKey);

                                //fund ratings handle
                                if (headerKey === 'fundRating' || headerKey === 'intFundRating') {
                                    return (
                                        <Box key={headerIndex} sx={{ textAlign: "center" }}>
                                            <Typography variant="overline_400" sx={{ fontSize: "9px", mb: "2px" }}>
                                                {headerLabel}
                                            </Typography>
                                            {value && value !== "Not Rated" && !Number.isNaN(Number(value)) && Number(value) > 0 ? (
                                                <Box sx={(theme: Theme) => ({
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: '3px',
                                                    border: `1px solid ${theme.palette.customColors.pastelGrey}`,
                                                    borderRadius: '20px',
                                                    padding: '6px 8px',
                                                    width: 'fit-content',
                                                    mx: 'auto'
                                                })}>
                                                    <Typography variant="overline_400" sx={{ fontSize: "15px", }}>
                                                        {Math.floor(Number(value))}
                                                    </Typography>
                                                    <GoldenStarFilled width={{ xs: '12', sm: '16' }} height={{ xs: '12', sm: '16' }} />
                                                </Box>
                                            ) : (
                                                <Typography variant="overline_400" sx={{ fontSize: "12px" }}>
                                                    Not Rated
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                }

                                //returns hadling will done
                                if (headerKey.includes('Return') || headerKey.includes('return')) {
                                    return (
                                        <Box key={headerIndex} sx={{ textAlign: "center" }}>
                                            <Typography variant="overline_400" sx={{ fontSize: "9px", mb: "2px" }}>
                                                {headerLabel}
                                            </Typography>
                                            <Typography
                                                variant="subtitle_600"
                                                sx={(theme: Theme) => ({
                                                    color:
                                                        value && Number(value) > 0
                                                            ? theme.palette.success.main
                                                            : theme.palette.error.main,
                                                    fontSize: "12px",
                                                })}
                                            >
                                                {value && !Number.isNaN(Number(value))
                                                    ? `${Number(value).toFixed(2)}%`
                                                    : "0%"}
                                            </Typography>
                                        </Box>
                                    );
                                }

                                return (
                                    <Box key={headerIndex} sx={{ textAlign: "center" }}>
                                        <Typography variant="overline_400" sx={{ fontSize: "9px", mb: "2px" }}>
                                            {headerLabel}
                                        </Typography>
                                        <Typography variant="subtitle_600" sx={{ fontSize: "12px" }}>
                                            {value}
                                        </Typography>
                                    </Box>
                                )
                            })}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

